import React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Dragger from 'antd/lib/upload/Dragger';
import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import { globalMessage } from '../../utils/globalMessage';
import { antGrey, runwayColors } from '../styles/globalStyles';
import FormItem from 'antd/lib/form/FormItem';
import Form from 'antd/lib/form/Form';
import Password from 'antd/lib/input/Password';
import { RcFile } from 'antd/lib/upload';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppStoreType } from '../../selectors/selectors';
import Input from 'antd/lib/input/Input';
import { AppStoreType } from '../../types';
import { FormInstance } from 'antd/lib/form';

export interface UploadFormParams {
    signingKeyPassword?: string;
    keystoreFilePassword?: string;
    keyAlias?: string;
    file: RcFile;
}
export interface UploadSigningKeyFormFieldsProps extends AppComponentProps {
    onUpload: (values: UploadFormParams) => void;
    prefixContent?: React.ReactNode;
    extraContent?: React.ReactNode;
    form: FormInstance;
    onSetRcFile?: (rcFile: RcFile) => void;
}

export const UploadSigningKeyFormFields: React.FC<UploadSigningKeyFormFieldsProps> = ({
    appId,
    onUpload,
    extraContent,
    prefixContent,
    form,
    onSetRcFile,
}) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isAppleAppStore = appStoreType == AppStoreType.Apple;

    const [rcFile, setRcFile] = React.useState<RcFile>();

    const dropdownTooltip =
        isAppleAppStore ?
            `Your distribution signing certificate and private key pair`
        :   `The signing key you use to sign your app, or the upload key you use to upload builds to the Play Console`;
    const dropdownLabel =
        isAppleAppStore ? `Distribution signing key (.p12)` : `Signing key or upload key .keystore or .jks file`;

    return (
        <>
            <Form
                form={form}
                onFinish={(values) => {
                    if (!rcFile) {
                        globalMessage.error('No file was provided');
                        return;
                    }

                    onUpload({ file: rcFile, ...values });
                }}
                initialValues={{}}
                autoComplete="off"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                }}
                layout="vertical"
            >
                {prefixContent}
                <Dragger
                    accept={isAppleAppStore ? '.p12' : '.keystore, .jks'}
                    multiple={false}
                    beforeUpload={(file) => {
                        // prevent default upload
                        setRcFile(file);
                        onSetRcFile?.(file);
                        return false;
                    }}
                    onRemove={() => {
                        setRcFile(undefined);
                    }}
                    maxCount={1}
                    style={{
                        borderWidth: 4,
                        marginBottom: 20,
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            height: 120,
                            alignItems: 'center',
                        }}
                    >
                        <Space direction="vertical">
                            <Space>
                                <Text style={{ color: antGrey.grey8, fontSize: 16 }}>{dropdownLabel}</Text>
                                <Tooltip title={dropdownTooltip}>
                                    <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                </Tooltip>
                            </Space>
                            <Text style={{ color: antGrey.grey7 }}>Click or drag to upload</Text>
                        </Space>
                    </div>
                </Dragger>
                {isAppleAppStore ?
                    <>
                        <FormItem
                            name="signingKeyPassword"
                            label={
                                <Space>
                                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Key password</Text>
                                    <Tooltip title={`Your distribution signing key password`}>
                                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                    </Tooltip>
                                </Space>
                            }
                        >
                            <Password placeholder="********" />
                        </FormItem>
                    </>
                :   <>
                        <FormItem
                            name="keystoreFilePassword"
                            label={
                                <Space>
                                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Keystore file password</Text>
                                </Space>
                            }
                        >
                            <Password placeholder="********" />
                        </FormItem>

                        <FormItem
                            name="keyAlias"
                            label={
                                <Space>
                                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Key alias</Text>
                                </Space>
                            }
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            name="keyPassword"
                            label={
                                <Space>
                                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Key password</Text>
                                </Space>
                            }
                        >
                            <Password placeholder="********" />
                        </FormItem>
                    </>
                }
                {extraContent}
            </Form>
        </>
    );
};
